// 添付ファイル（§12）。参照用副本。アップロード/一覧/ダウンロード(認証経由)/削除。
import { useEffect, useRef, useState } from "react";
import { Button, Spinner } from "../../components/ui";
import { formatDate } from "../../lib/format";
import { api, ApiError } from "../../lib/api";
import { t } from "../../i18n/zh-TW";
import type { Attachment } from "../../lib/types";

function formatSize(bytes: number | null): string {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function AttachmentSection({
  contractId,
  currentEmail,
  canManage,
}: {
  contractId: number;
  currentEmail: string;
  canManage: boolean; // 経営側は他人の添付も削除可
}) {
  const [items, setItems] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  function load() {
    setLoading(true);
    api
      .listAttachments(contractId)
      .then((res) => setItems(res.attachments))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : t.errors.loadFailed))
      .finally(() => setLoading(false));
  }

  useEffect(load, [contractId]);

  async function onUpload(file: File) {
    setBusy(true);
    setError(null);
    try {
      const res = await api.uploadAttachment(contractId, file);
      setItems((prev) => [res.attachment, ...prev]);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t.errors.saveFailed);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function onDownload(a: Attachment) {
    setError(null);
    try {
      await api.downloadAttachment(a.id, a.filename);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "下載失敗。");
    }
  }

  async function onDelete(a: Attachment) {
    if (!window.confirm(t.attachments.deleteConfirm)) return;
    setError(null);
    try {
      await api.deleteAttachment(a.id);
      setItems((prev) => prev.filter((x) => x.id !== a.id));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t.errors.saveFailed);
    }
  }

  return (
    <div>
      <p className="mb-2 text-xs text-slate-500">{t.attachments.note}</p>

      {loading ? (
        <Spinner label={t.common.loading} />
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400">{t.attachments.empty}</p>
      ) : (
        <ul className="space-y-1">
          {items.map((a) => (
            <li
              key={a.id}
              className="flex items-center justify-between rounded-lg bg-white px-3 py-2 shadow-sm"
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-slate-800">{a.filename}</div>
                <div className="text-xs text-slate-400">
                  {formatSize(a.size)}・{a.uploaded_by}・{formatDate(a.created_at)}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  onClick={() => onDownload(a)}
                  className="text-sm text-brand-600 hover:underline"
                >
                  {t.attachments.download}
                </button>
                {(canManage || a.uploaded_by === currentEmail) && (
                  <button
                    onClick={() => onDelete(a)}
                    className="text-sm text-red-600 hover:underline"
                  >
                    {t.attachments.delete}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="mt-2 text-sm text-red-700">{error}</p>}

      <div className="mt-3">
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onUpload(f);
          }}
        />
        <Button
          variant="secondary"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
        >
          {busy ? t.attachments.uploading : t.attachments.upload}
        </Button>
      </div>
    </div>
  );
}
